import React, { useEffect, useRef, useState } from "react";
import DropdownButton from "../DropdownButton";
import styles from "./styles.module.scss";
import check_icon from "app/images/blue_check.svg";
import DatePicker from "../DatePicker/InputDatePicker";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import Button from "../Button/ButtonVariant";
import { FormProvider, useForm } from "react-hook-form";
import AthleteFilterSelector from "./AthleteFilterSelector";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import SelectionList from "../SchedulingUserSelector/SelectionList";
import ResetFiltersButton from "./ResetFiltersButton";
import CheckBox from "../inputs/CheckBox";
import { cls } from "app/utils";

const DEFAULT_ACTIONS = {
  create: false,
  update: false,
  delete: false,
};

type LogsFiltersProps = {
  setLogsQuery: React.Dispatch<React.SetStateAction<{
    perPage: number;
    from?: string;
    to?: string;
    user_ids?: string[];
    log_types?: string[];
  }>>;
};

const LogsFilters: React.FC<LogsFiltersProps> = ({ setLogsQuery }) => {
  const { t } = useTranslation();

  const TimeRange = {
    last10Days: t('Last 10 Days'),
    last30Days: t('Last 30 Days'),
    custom: t('Custom Range'),
  };

  const Actions = {
    create: t('New Session'),
    update: t('Updated Session'),
    delete: t('Canceled Session'),
  };

  const [timeRange, setTimeRange] = useState(TimeRange.last30Days);
  const [startDate, setStartDate] = useState<DateTime>(DateTime.now().minus({ days: 30 }));
  const [endDate, setEndDate] = useState<DateTime>(DateTime.now());
  const [closeDropdown, setCloseDropdown] = useState(false);
  const [totalAthletes, setTotalAthletes] = useState<number>(0);
  const [actions, setActions] = useState(DEFAULT_ACTIONS);

  const methods = useForm({
    defaultValues: {
      studentProfiles: [],
    },
    mode: 'onChange',
  });
  const currentFacility = useCurrentFacility();
  const facilityId = currentFacility.data?.id;
  const watchStudents = methods.watch("studentProfiles");
  const showSelectionList = watchStudents.length > 0;

  const [activeDropdown, setActiveDropdown] = useState<null | 'time' | 'athletes' | 'actions'>(null);
  const dropdownRefs = {
    time: useRef<HTMLDivElement>(null),
    athletes: useRef<HTMLDivElement>(null),
    actions: useRef<HTMLDivElement>(null),
  };

  const onRemove = (userId: number) => {
    const current = methods.getValues("studentProfiles");
    const updated = current.filter((user) => user.id !== userId);
    methods.setValue("studentProfiles", updated);

    onFilterbyAthletes();
  };

  const onCancel = () => {
    onChangeTimeRange(TimeRange.last30Days);
  };

  const onAthletesCancel = () => {
    methods.setValue("studentProfiles", []);
    setLogsQuery((prev) => ({
      ...prev,
      user_ids: [],
    }));
    setCloseDropdown((prev) => !prev);
    setActiveDropdown(null);
  };

  const onActionsCancel = () => {
    setActions(DEFAULT_ACTIONS);
    setLogsQuery((prev) => ({
      ...prev,
      log_types: [],
    }));
    setCloseDropdown((prev) => !prev);
    setActiveDropdown(null);
  };

  const onFilter = () => {
    setLogsQuery((prev) => ({
      ...prev,
      from: startDate.toISODate(),
      to: endDate.toISODate(),
    }));
    setCloseDropdown((prev) => !prev);
    setActiveDropdown(null);
  };

  const onFilterbyAthletes = () => {
    const selectedAthletes = methods.getValues("studentProfiles");
    const userIds = selectedAthletes.map((athlete: { id: string }) => athlete.id);


    setLogsQuery((prev) => ({
      ...prev,
      user_ids: userIds,
    }));

    setCloseDropdown((prev) => !prev);
  };

  const onChangeTimeRange = (range: string) => {
    setTimeRange(range);

    const now = DateTime.now();
    let from: string;
    let to: string;

    if (range === TimeRange.last10Days) {
      from = now.minus({ days: 10 }).toISODate();
      to = now.toISODate();
    } else if (range === TimeRange.last30Days) {
      from = now.minus({ days: 30 }).toISODate();
      to = now.toISODate();
    } else if (range === TimeRange.custom) {
      from = startDate.toISODate();
      to = endDate.toISODate();
    }

    setLogsQuery((prev) => ({
      ...prev,
      from,
      to,
    }));

    if (range !== TimeRange.custom) {
      setCloseDropdown((prev) => !prev);
      setActiveDropdown(null);
    }
  };

  const getFilterText = (type: 'Athletes' | 'Actions', count: number, total: number) => {
    if (count === total && total > 0 || count === 0) {
      return t(`All ${type}`);
    }

    return t(`count_${type.toLowerCase()}`, { count });
  };

  const resetFilters = () => {
    const now = DateTime.now();
    const from = now.minus({ days: 30 }).toISODate();
    const to = now.toISODate();

    setTimeRange(TimeRange.last30Days);
    methods.setValue("studentProfiles", []);
    setActions(DEFAULT_ACTIONS);

    setLogsQuery((prev) => ({
      ...prev,
      from,
      to,
      user_ids: [],
      log_types: [],
    }));
  };

  const selectAction = (key: string) => {
    setActions(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const onFilterByActions = () => {
    const actions_types = Object.keys(actions).filter(key => actions[key]);

    setLogsQuery((prev) => ({
      ...prev,
      log_types: actions_types,
    }));

    setCloseDropdown((prev) => !prev);
    setActiveDropdown(null);
  };

  const getSelectedAthletesLegth = () => {
    return methods.getValues("studentProfiles").length;
  };

  const getSelectedActionsLength = () => {
    return Object.values(actions).filter(Boolean).length;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!activeDropdown) {
        return;
      }

      const target = event.target as Node;
      const currentRef = dropdownRefs[activeDropdown];

      if (
        currentRef.current &&
        !currentRef.current.contains(target)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown]);

  const displayResetButton = () => {
    return timeRange !== TimeRange.last30Days || getSelectedAthletesLegth() > 0 || getSelectedActionsLength() > 0;
  };

  return (
    <FormProvider {...methods}>
      <div className={styles.filters_wrapper}>
        <div className={styles.filters_row}>
          <div className={styles.container}>
            <div ref={dropdownRefs.time}>
              <DropdownButton
                hideButtonStyles
                btnContent={(
                  <div className={styles.selected_value}>
                    <p>{timeRange}</p>
                    <i className={cls("ico ico-chevron", { [styles.rotated]: activeDropdown === 'time' })} />
                  </div>
                )}
                dropdownWrapperClass={styles.wrapper}
                dropdownClassname={styles.dropdown}
                closeModal={closeDropdown}
                onOpen={() => setActiveDropdown(activeDropdown === 'time' ? null : 'time')}
              >
                {Object.values(TimeRange).map((range) => (
                  <div key={range} className={styles.list_item} onClick={() => onChangeTimeRange(range)}>
                    <p>{range}</p>
                    {range === timeRange && <img src={check_icon} />}
                  </div>
                ))}
                {timeRange === TimeRange.custom && (
                  <div className={styles.custom_date}>
                    <DatePicker
                      date={startDate}
                      minDate={DateTime.now().minus({ year: 1 }).toJSDate()}
                      maxDate={endDate.toJSDate()}
                      setDate={(date) => setStartDate(date)}
                      placeholderText="Select Date"
                    />
                    <span>-</span>
                    <DatePicker
                      date={endDate}
                      minDate={startDate.toJSDate()}
                      maxDate={DateTime.now().toJSDate()}
                      setDate={(date) => setEndDate(date)}
                      placeholderText="Select Date"
                    />
                  </div>
                )}
                {timeRange === TimeRange.custom && (
                  <div className={styles.cancel_button_wrapper}>
                    <Button
                      title={t("Cancel")}
                      buttonType="light-primary"
                      onClickFunc={onCancel}
                    />
                    <Button
                      title={t("Filter")}
                      buttonType="primary"
                      onClickFunc={onFilter}
                    />
                  </div>
                )}
              </DropdownButton>
            </div>

            <div ref={dropdownRefs.athletes}>
              <DropdownButton
                hideButtonStyles
                btnContent={(
                  <div className={cls({ [styles.inactive_filter]: getSelectedAthletesLegth() === 0 }, styles.selected_value)}>
                    <p>
                      {getFilterText('Athletes', getSelectedAthletesLegth(), totalAthletes)}
                    </p>
                    <i className={cls("ico ico-chevron", { [styles.rotated]: activeDropdown === 'athletes' })} />
                  </div>
                )}
                dropdownWrapperClass={styles.wrapper}
                dropdownClassname={styles.dropdown}
                closeModal={closeDropdown}
                onOpen={() => setActiveDropdown(activeDropdown === 'athletes' ? null : 'athletes')}
              >
                {facilityId && (
                  <>
                    <div className={styles.form}>
                      <form>
                        <AthleteFilterSelector
                          facilityId={currentFacility.data?.id}
                          canAddNewAthletes={false}
                          showList={false}
                          setTotalAthletes={setTotalAthletes}
                        />
                      </form>
                    </div>
                    <div className={styles.cancel_button_wrapper}>
                      <Button
                        title={t("Cancel")}
                        buttonType="light-primary"
                        onClickFunc={onAthletesCancel}
                      />
                      <Button
                        title={t("Filter")}
                        buttonType="primary"
                        onClickFunc={onFilterbyAthletes}
                      />
                    </div>
                  </>
                )}
              </DropdownButton>
            </div>

            <div ref={dropdownRefs.actions}>
              <DropdownButton
                hideButtonStyles
                btnContent={(
                  <div className={cls({ [styles.inactive_filter]: getSelectedActionsLength() === 0 }, styles.selected_value)}>
                    <p>{getFilterText('Actions', getSelectedActionsLength(), Object.values(actions).length)}</p>
                    <i className={cls("ico ico-chevron", { [styles.rotated]: activeDropdown === 'actions' })} />
                  </div>
                )}
                dropdownWrapperClass={styles.wrapper}
                dropdownClassname={styles.dropdown}
                closeModal={closeDropdown}
                onOpen={() => setActiveDropdown(activeDropdown === 'actions' ? null : 'actions')}
              >
                <div className={styles.actions_dropdown}>
                  {Object.entries(Actions).map(([key, value]) => (
                    <div key={key} className={styles.actions_list_item} onClick={() => selectAction(key)}>
                      <p>{value}</p>
                      <CheckBox
                          key={key}
                          name={value}
                          className={styles.checkbox_input}
                          checked={actions[key]}
                        />
                    </div>
                  ))}
                </div>
                <div className={styles.cancel_button_wrapper}>
                  <Button
                    title={t("Cancel")}
                    buttonType="light-primary"
                    onClickFunc={onActionsCancel}
                  />
                  <Button
                    title={t("Filter")}
                    buttonType="primary"
                    onClickFunc={onFilterByActions}
                  />
                </div>
              </DropdownButton>
            </div>
          </div>
          {displayResetButton() && <ResetFiltersButton onClick={resetFilters} />}
        </div>
      </div>
      {showSelectionList && <SelectionList selection={watchStudents} onRemove={onRemove} pillStyle />}
    </FormProvider>
  );
};

export default LogsFilters;
